// Employer plan types eligible for contributions: 401k, 401a, 403b, 457, and Keogh.
// Keep in sync with `store.employerAccount`.
const EMPLOYER_PLAN_TYPES = [1, 23, 2, 4, 6]

// Generic engine category used for each participant's primary employer plan.
const GENERIC_CATEGORY = '401k401a'

// Category priority used to identify each participant's primary plan.
const PLAN_CATEGORIES = [GENERIC_CATEGORY, '403b', '457']

const planCategory = planType => {
  if (planType === 2) return '403b'
  if (planType === 4) return '457'
  return GENERIC_CATEGORY
}

// Matches `InstitutionalAccount.hasEmployerMatch`: an eligible account with a
// nonzero rate or dollar match.
const accountHasEmployerMatch = account =>
  !!(account.contributionEligibility && (account.companyMatchRate || account.companyMatchDlrMax))

// Maps each plan category to its percent and annual-dollar engine fields.
// Keep pre-tax → Roth → after-tax order so the copy appears in that order.
const BUCKET_FIELDS = {
  '401k401a': [
    {
      label: 'pre-tax',
      percent: 'preTaxAnnualContributions',
      dollars: 'preTaxAnnualContribDollars',
    },
    {
      label: 'Roth',
      percent: 'rothAnnualContributions',
      dollars: 'rothAnnualContribDollars',
    },
    {
      label: 'after-tax',
      percent: 'postTaxAnnualContributions',
      dollars: 'postTaxAnnualContribDollars',
    },
  ],
  '403b': [
    {
      label: 'pre-tax',
      percent: 'preTax403bAnnualContributions',
      dollars: 'preTax403bAnnualContribDollars',
    },
  ],
  '457': [
    {
      label: 'pre-tax',
      percent: 'preTax457AnnualContributions',
      dollars: 'preTax457AnnualContribDollars',
    },
    {
      label: 'Roth',
      percent: 'roth457AnnualContributions',
      dollars: 'roth457AnnualContribDollars',
    },
  ],
}

// Percent values are unchanged; annual dollar values are converted to monthly amounts.
const bucketsForPlan = ({ baseCase, recommendedCase, isSpouse, category, isPercentOfPay }) => {
  const displayValue = (engineCase, field) => {
    const enginePerson = isSpouse ? engineCase?.spouse : engineCase?.primary
    if (!enginePerson) return 0

    return isPercentOfPay
      ? (enginePerson[field.percent] ?? 0)
      : (enginePerson[field.dollars] ?? 0) / 12
  }

  return BUCKET_FIELDS[category].map(field => ({
    label: field.label,
    current: displayValue(baseCase, field),
    recommended: displayValue(recommendedCase, field),
  }))
}

// Engine case fields follow `totalAnnualSavings[Spouse]<category>` and
// `totalAnnualPercentage[Spouse]<category>`.
const contributionForCategory = (engineCase, isSpouse, category) => {
  const owner = isSpouse ? 'Spouse' : ''

  return {
    annualDollars: engineCase[`totalAnnualSavings${owner}${category}`],
    percent: engineCase[`totalAnnualPercentage${owner}${category}`],
  }
}

// Category totals do not overlap, so they can be summed safely.
const employerAnnualSaving = (engineCase, includeSpouse) => {
  const ownerTotal = isSpouse =>
    PLAN_CATEGORIES.reduce(
      (sum, category) =>
        sum + (contributionForCategory(engineCase, isSpouse, category).annualDollars ?? 0),
      0
    )

  return ownerTotal(false) + (includeSpouse ? ownerTotal(true) : 0)
}

// Keep the institutional plan first, followed by the participant's other plans and spouse plans.
// The engine provides one contribution total per owner/category, so combine account names
// when multiple accounts occupy the same slot.
const enumerateEmployerAccounts = (store, person, includeSpouse) => {
  const { institutional } = store
  const employerAccounts = []

  if (
    institutional?.contributionEligibility &&
    EMPLOYER_PLAN_TYPES.includes(institutional.planType)
  ) {
    employerAccounts.push(institutional)
  }

  const nonGcEmployerAccounts = store.nonGC
    .filter(
      account =>
        account.contributionEligibility &&
        EMPLOYER_PLAN_TYPES.includes(account.planType) &&
        (includeSpouse || account.participantId === person.id)
    )
    .slice()
    .sort(
      (a, b) => (a.participantId === person.id ? 0 : 1) - (b.participantId === person.id ? 0 : 1)
    )

  employerAccounts.push(...nonGcEmployerAccounts)

  const bySlot = new Map()

  employerAccounts.forEach(account => {
    const slot = `${account.participantId}:${planCategory(account.planType)}`
    const existing = bySlot.get(slot)

    if (existing) {
      existing.accounts.push(account)
    } else {
      bySlot.set(slot, { accounts: [account] })
    }
  })

  // In retail the institutional account is the GuidedChoice retail wrapper, not the
  // payroll employer plan. When it shares a slot with the participant's own plan,
  // drop it so the label uses the real plan name instead of "GC Retail Web + ...".
  return [...bySlot.values()].map(({ accounts }) => {
    const slotAccounts =
      store.config?.isRetail && accounts.length > 1
        ? accounts.filter(account => account !== institutional)
        : accounts
    const [account, ...rest] = slotAccounts

    // The account that carries the match may not be the first one in the slot
    // (e.g. an institutional plan with no match combined with a nonGC plan that
    // does), so surface the first match-bearing account for the match row.
    const matchAccount = slotAccounts.find(accountHasEmployerMatch) ?? null

    return { account, additionalNames: rest.map(other => other.name), matchAccount }
  })
}

// GC does not recommend IRA changes, so an IRA's current and recommended
// amounts are always the same. IRAs are excluded from the engine's own totals,
// so they're summed separately and added into the heading totals below.
// Group the participant's IRAs before the spouse's without changing their existing order.
// Matches enumerateEmployerAccounts: exclude the spouse's IRAs unless the spouse is included.
const buildIraRows = (accountInsight, person, includeSpouse) =>
  (accountInsight?.allIras ?? [])
    .filter(
      ira =>
        (ira.annualContributionDollars ?? 0) > 0 &&
        (includeSpouse || ira.participantId === person.id)
    )
    .sort(
      (a, b) => (a.participantId === person.id ? 0 : 1) - (b.participantId === person.id ? 0 : 1)
    )
    .map(ira => ({
      id: `ira-${ira.accountId}`,
      name: ira.accountName ?? '',
      currentMonthly: (ira.annualContributionDollars ?? 0) / 12,
    }))

export function buildContributionsViewModel(store) {
  const { baseCase, person, includeSpouseInPlanning, spouse, accountInsight } = store
  const includeSpouse = !!includeSpouseInPlanning
  const isModified = !!store.modifiedCase
  const recommendedCase = store.modifiedCase || store.recommendedCase

  const employerAccounts = enumerateEmployerAccounts(store, person, includeSpouse)

  // The engine stores a person's primary employer plan in the generic 401k/401a
  // fields, even when that plan is a 403b or 457. Additional plans use their
  // category-specific fields.
  const primaryCategoryByParticipant = new Map()

  employerAccounts.forEach(({ account }) => {
    const category = planCategory(account.planType)
    const existing = primaryCategoryByParticipant.get(account.participantId)

    if (
      existing === undefined ||
      PLAN_CATEGORIES.indexOf(category) < PLAN_CATEGORIES.indexOf(existing)
    ) {
      primaryCategoryByParticipant.set(account.participantId, category)
    }
  })

  const employerPlans = employerAccounts.map(({ account, additionalNames, matchAccount }) => {
    const isSpouse = account.participantId !== person.id
    const category = planCategory(account.planType)
    const isRetailDummyPlan = store.config?.isRetail && account === store.institutional
    const planName = isRetailDummyPlan
      ? 'retirement plan'
      : [account.name, ...additionalNames].join(' + ')
    const sourceCategory =
      category === primaryCategoryByParticipant.get(account.participantId)
        ? GENERIC_CATEGORY
        : category
    const current = contributionForCategory(baseCase, isSpouse, sourceCategory)
    const recommended = contributionForCategory(recommendedCase, isSpouse, sourceCategory)
    const ownerName = isSpouse ? (spouse?.firstName ?? '') : person.displayName
    const hasEmployerMatch = !!matchAccount

    // Check the recommended contribution with `>=`, matching the guide's existing check.
    // `contributeMax` checks the current rate with strict `>`. The combined engine total
    // is compared against the match-bearing account's maximum.
    const isMaxingMatch =
      hasEmployerMatch &&
      (matchAccount.contribMethod === 'D'
        ? (recommended.annualDollars ?? 0) >= (matchAccount.companyMatchDlrMax ?? 0)
        : (recommended.percent ?? 0) >= (matchAccount.companyMatchRateMax ?? 0))

    const currentIsMaxingMatch =
      hasEmployerMatch &&
      (matchAccount.contribMethod === 'D'
        ? (current.annualDollars ?? 0) >= (matchAccount.companyMatchDlrMax ?? 0)
        : (current.percent ?? 0) >= (matchAccount.companyMatchRateMax ?? 0))

    const isPercentOfPay = account.contribMethod === 'P'

    return {
      id: `${account.type}-${account.id}`,
      ownerLabel: includeSpouse ? `${ownerName}'s ${planName}` : `Your ${planName}`,
      isPercentOfPay,
      currentMonthly: (current.annualDollars ?? 0) / 12,
      currentPercent: current.percent ?? 0,
      recommendedMonthly: (recommended.annualDollars ?? 0) / 12,
      recommendedPercent: recommended.percent ?? 0,
      hasEmployerMatch,
      isMaxingMatch,
      currentIsMaxingMatch,
      buckets: bucketsForPlan({
        baseCase,
        recommendedCase,
        isSpouse,
        category: sourceCategory,
        isPercentOfPay,
      }),
    }
  })

  const outOfPlanMonthlySavings = recommendedCase.outOfPlanMonthlySavings ?? 0
  const iras = buildIraRows(accountInsight, person, includeSpouse)
  const iraMonthlyTotal = iras.reduce((sum, ira) => sum + ira.currentMonthly, 0)
  const recommendedMonthlyTotal =
    employerAnnualSaving(recommendedCase, includeSpouse) / 12 +
    outOfPlanMonthlySavings +
    iraMonthlyTotal
  const currentMonthlyTotal = employerAnnualSaving(baseCase, includeSpouse) / 12 + iraMonthlyTotal

  // Show a percentage only when no spouse is included and the participant's savings
  // consist entirely of percent-of-pay employer contributions.
  const isPercentOnly =
    !includeSpouse &&
    employerPlans.length > 0 &&
    employerPlans.every(plan => plan.isPercentOfPay) &&
    outOfPlanMonthlySavings === 0 &&
    iras.length === 0

  const aggregatePercent = PLAN_CATEGORIES.reduce(
    (sum, category) =>
      sum + (contributionForCategory(recommendedCase, false, category).percent ?? 0),
    0
  )

  return {
    includeSpouse,
    isModified,
    employerPlans,
    iras,
    outOfPlanMonthlySavings,
    currentMonthlyTotal,
    recommendedMonthlyTotal,
    isPercentOnly,
    aggregatePercent,
  }
}
